import { ListItemIcon, Stack, Tooltip } from '@mui/material'
import Link from 'next/link'
import { forwardRef, PropsWithChildren, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CountBadge } from '@omnea-app/omui/CountBadge'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { MenuItemType } from './SidebarUncontrolled'
import { SidebarBadge, SidebarListItemButton, SidebarListItemText } from './StyledSidebarComponents'

type SidebarLinkProps = Omit<MenuItemType, 'type' | 'children'> &
  PropsWithChildren & { isSidebarExpanded: boolean; onClick?: () => void }

export function SidebarLink({
  isSidebarExpanded,
  selected,
  Icon,
  title,
  children,
  href,
  count,
  badge,
  onClick,
}: SidebarLinkProps) {
  const { t } = useTranslation('common')

  const showCount = isDefined(count) && count > 0
  const showBadge = isDefined(badge)

  // HACK: we cache the component so the tooltip does not lose reference to it between re-renders
  const ListIcon = useMemo(
    () =>
      forwardRef(function ListIconInner(props: any, ref: any) {
        return (
          <ListItemIcon sx={{ minWidth: 24, minHeight: 24 }} {...props} ref={ref}>
            <Icon sx={{ width: 18, color: 'sidebar.icon' }} />
          </ListItemIcon>
        )
      }),
    [Icon]
  )
  return (
    <Link href={href} passHref>
      <SidebarListItemButton selected={selected} onClick={onClick}>
        {isSidebarExpanded ? (
          <ListIcon />
        ) : (
          <Tooltip title={title}>
            <ListIcon />
          </Tooltip>
        )}
        {isSidebarExpanded &&
          (showBadge ? (
            <Stack direction="row" gap={2.5} alignItems="center" flexGrow={1}>
              <SidebarListItemText primary={title} sx={{ flexGrow: 0 }} />
              <CountBadge count={badge} color="mint" />
            </Stack>
          ) : (
            <SidebarListItemText primary={title} />
          ))}
        {showCount && (
          <Tooltip title={t(`sidebar.item-no-tooltip${count === 1 ? '_one' : ''}`, { count })}>
            <SidebarBadge sx={!isSidebarExpanded ? { ml: '-14px', mt: '-14px' } : {}}>{count}</SidebarBadge>
          </Tooltip>
        )}
        {children}
      </SidebarListItemButton>
    </Link>
  )
}
